import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import {
  Customized,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
  useChartHeight,
  useChartWidth,
} from 'recharts';
import Loading from 'components/loading/Loading';
import DisplayError from 'components/error/DisplayError';
import { UiIcon, UiTooltip } from 'ui-components';
import { commaSeperator } from 'utils';
import { isEmpty } from 'lib';
import { StyledGauge } from './Gauge.styles';

/** Recharts measures angles from east, counter-clockwise: 180 is west, 90 is north. */
const START_DEGREES = 180;
const END_DEGREES = 0;
const HALF_TURN_DEGREES = 180;
const QUARTER_TURN_DEGREES = 90;

/** A half circle needs the box twice as wide as it is tall. */
const CHART_ASPECT = 2;
const NO_MARGIN = { top: 0, right: 0, bottom: 0, left: 0 };

/** Chrome tones for the needle and its pivot — fixed regardless of what the gauge measures. */
const NEEDLE_COLOR = '#C4C4C4';
const NEEDLE_PIVOT_COLOR = '#EDEDED';
const TICK_COLOR = '#777777';

/**
 * Every gauge part is a share of the outer radius so the drawing scales with
 * the card. The ratios come from the Figma frame this shape was matched
 * against, where the outer radius is 100: the needle is 42.86 long, 5.15 wide
 * at its base and 1.29 at its tip. The Figma frame pivots the needle 11.43
 * below the arc's own centre, but rotating around anywhere off-centre bends
 * the value→angle mapping (the further from the middle, the more the needle
 * droops past where it should point) — so, unlike Figma, the pivot here sits
 * exactly at the arc's centre, which keeps that mapping linear at every value.
 */
const INNER_RADIUS_RATIO = 0.645;
const NEEDLE_LENGTH_RATIO = 0.4286;
const NEEDLE_BASE_HALF_RATIO = 0.0258;
const NEEDLE_TIP_HALF_RATIO = 0.0064;
const PIVOT_RADIUS_RATIO = 0.0571;
const TICK_LENGTH_RATIO = 0.0571;
const TICK_LABEL_GAP_RATIO = 0.046;
const EDGE_LABEL_OFFSET_RATIO = 0.02;
const MID_LABEL_BASELINE_RATIO = 0.466;
const LABEL_FONT_SIZE = 10;

/**
 * Recharts turns a Pie's percentage radius into pixels against
 * `min(width, height) / 2`. The gauge's box keeps a 2:1 aspect ratio (see
 * `CHART_ASPECT`), so that denominator is `height / 2` — half of the
 * `height` the dial itself draws its outer radius at — so every percentage
 * here is doubled to land on the same pixels as `GaugeDial`.
 */
const HOVER_OUTER_RADIUS = '200%';
const HOVER_PIE_DATA = [{ value: 1 }];

const clampValue = (value, min, max) =>
  Math.min(Math.max(value ?? min, min), max);

/** Evenly spaces the given colors as gradient stops; a single color still renders flat. */
const toGradientStops = (colors) => {
  const stops = colors?.length ? colors : ['#CCCCCC'];
  if (stops.length === 1) return [{ offset: '0%', color: stops[0] }];
  return stops.map((color, index) => ({
    offset: `${(index / (stops.length - 1)) * 100}%`,
    color,
  }));
};

/**
 * The whole dial is drawn through Recharts' `Customized` layer so the arc, the
 * needle and the scale marks share one pixel coordinate space and keep the
 * exact proportions of the design. Recharts 3 no longer hands the chart size
 * down as props, so it is read from the chart context.
 */
const GaugeDial = ({
  value = 0,
  min = 0,
  max = 100,
  gradientId = '',
  minLabel = '',
  maxLabel = '',
  midLabel = null,
}) => {
  const width = useChartWidth() ?? 0;
  const height = useChartHeight() ?? 0;
  const cx = width / 2;
  const cy = height;
  const outerRadius = width / 2;
  const innerRadius = outerRadius * INNER_RADIUS_RATIO;
  const length = outerRadius * NEEDLE_LENGTH_RATIO;
  const baseHalf = outerRadius * NEEDLE_BASE_HALF_RATIO;
  const tipHalf = outerRadius * NEEDLE_TIP_HALF_RATIO;
  const tick = outerRadius * TICK_LENGTH_RATIO;
  const labelGap = outerRadius * TICK_LABEL_GAP_RATIO;
  const edgeLabelY = cy - outerRadius * EDGE_LABEL_OFFSET_RATIO;
  /** -90° (west, at `min`) through 0° (north, at the midpoint) to +90° (east, at `max`). */
  const angle =
    ((value - min) / (max - min)) * HALF_TURN_DEGREES - QUARTER_TURN_DEGREES;

  const needlePath = [
    `M ${cx - baseHalf} ${cy}`,
    `L ${cx - tipHalf} ${cy - length}`,
    `L ${cx + tipHalf} ${cy - length}`,
    `L ${cx + baseHalf} ${cy}`,
    'Z',
  ].join(' ');

  return (
    <g className='gauge-dial'>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={START_DEGREES}
        endAngle={END_DEGREES}
        fill={`url(#${gradientId})`}
        stroke='none'
      />
      <line
        x1={cx - innerRadius}
        y1={cy}
        x2={cx - innerRadius + tick}
        y2={cy}
        stroke={TICK_COLOR}
      />
      <line
        x1={cx + innerRadius - tick}
        y1={cy}
        x2={cx + innerRadius}
        y2={cy}
        stroke={TICK_COLOR}
      />
      <text
        className='gauge-scale-label'
        x={cx - innerRadius + tick + labelGap}
        y={edgeLabelY}
        textAnchor='start'
        fontSize={LABEL_FONT_SIZE}
      >
        {minLabel}
      </text>
      <text
        className='gauge-scale-label'
        x={cx + innerRadius - tick - labelGap}
        y={edgeLabelY}
        textAnchor='end'
        fontSize={LABEL_FONT_SIZE}
      >
        {maxLabel}
      </text>
      {midLabel !== null && (
        <>
          <line
            x1={cx}
            y1={cy - innerRadius}
            x2={cx}
            y2={cy - innerRadius + tick}
            stroke={TICK_COLOR}
          />
          <text
            className='gauge-scale-label'
            x={cx}
            y={cy - outerRadius * MID_LABEL_BASELINE_RATIO}
            textAnchor='middle'
            fontSize={LABEL_FONT_SIZE}
          >
            {midLabel}
          </text>
        </>
      )}
      <path
        d={needlePath}
        fill={NEEDLE_COLOR}
        transform={`rotate(${angle} ${cx} ${cy})`}
      />
      <circle
        cx={cx}
        cy={cy}
        r={outerRadius * PIVOT_RADIUS_RATIO}
        fill={NEEDLE_PIVOT_COLOR}
      />
    </g>
  );
};

GaugeDial.propTypes = {
  value: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  gradientId: PropTypes.string,
  minLabel: PropTypes.string,
  maxLabel: PropTypes.string,
  midLabel: PropTypes.string,
};

/**
 * Recharts clones `Tooltip`'s `content` element with its own props, which
 * include a `content` key of its own (the element being cloned) — naming
 * this prop `hoverContent` instead of `content` keeps that clone from
 * clobbering it.
 */
const GaugeHoverTooltipContent = ({ active, hoverContent }) =>
  active ? hoverContent : null;

GaugeHoverTooltipContent.propTypes = {
  active: PropTypes.bool,
  hoverContent: PropTypes.node,
};

/**
 * A semicircular dial gauge (gradient arc + needle), generic over its value
 * range, colors, formatting and content — a Recharts-based, reusable
 * replacement for the deprecated bizcharts `Gauge`. Feature-specific gauges
 * (e.g. the net sentiment score) should wrap this rather than duplicate it.
 */
const Gauge = ({
  value = null,
  min = 0,
  max = 100,
  colors,
  formatValue = (val) => commaSeperator(val),
  minLabel = '',
  maxLabel = '',
  midLabel = null,
  title = '',
  tooltip = '',
  label = '',
  loading = false,
  error = null,
  noData = false,
  refresh,
  actions,
  legend = null,
  hoverTooltipContent = null,
  className = '',
  noHeader = false,
  'data-testid': dataTestId = 'gauge',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const gradientId = useId();
  const [hoverPosition, setHoverPosition] = useState(null);

  const clampedValue = clampValue(value, min, max);
  const gradientStops = toGradientStops(colors);

  /**
   * Recharts positions a polar-chart tooltip along the hovered slice's own
   * angle, which would pin it to one spot instead of tracking the cursor —
   * so the position is read off the raw mouse event and passed to `Tooltip`
   * directly instead.
   */
  const handleDialMouseMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setHoverPosition({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
  };

  let content;
  if (loading) {
    content = <Loading spinning={loading} unVisibleText={true} />;
  } else if (!isEmpty(error)) {
    content = <DisplayError error={error} />;
  } else if (noData || value === null || value === undefined) {
    content = <div className='no-data'>{t('common:dataNotFound')}</div>;
  } else {
    const dial = (
      <div className='gauge-hover-target'>
        {/*
         * ResponsiveContainer sizes its svg from the aspect but leaves its own
         * box at zero height, so the wrapper reserves the same ratio and keeps
         * the arc from painting over the rows underneath.
         */}
        <div className='gauge-chart' onMouseMove={handleDialMouseMove}>
          <ResponsiveContainer width='100%' aspect={CHART_ASPECT}>
            <PieChart margin={NO_MARGIN}>
              <defs>
                <linearGradient id={gradientId} x1='0' y1='0' x2='1' y2='0'>
                  {gradientStops.map((stop) => (
                    <stop
                      key={stop.offset}
                      offset={stop.offset}
                      stopColor={stop.color}
                    />
                  ))}
                </linearGradient>
              </defs>
              <Customized
                component={
                  <GaugeDial
                    value={clampedValue}
                    min={min}
                    max={max}
                    gradientId={gradientId}
                    minLabel={minLabel}
                    maxLabel={maxLabel}
                    midLabel={midLabel}
                  />
                }
              />
              {hoverTooltipContent && (
                <>
                  {/*
                   * An invisible full-dial slice, drawn on top of the arc so
                   * it wins the hit-test, gives Recharts an actual hovered
                   * item to key the tooltip's visibility off — the dial
                   * itself is drawn by `Customized` and isn't one.
                   */}
                  <Pie
                    data={HOVER_PIE_DATA}
                    dataKey='value'
                    cx='50%'
                    cy='100%'
                    startAngle={START_DEGREES}
                    endAngle={END_DEGREES}
                    innerRadius={0}
                    outerRadius={HOVER_OUTER_RADIUS}
                    fill='transparent'
                    stroke='none'
                    isAnimationActive={false}
                    legendType='none'
                  />
                  <Tooltip
                    cursor={false}
                    isAnimationActive={false}
                    position={hoverPosition ?? undefined}
                    content={
                      <GaugeHoverTooltipContent
                        hoverContent={hoverTooltipContent}
                      />
                    }
                  />
                </>
              )}
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className='gauge-summary'>
          <div className='gauge-value'>{formatValue(clampedValue)}</div>
          {label && <div className='gauge-label'>{label}</div>}
        </div>
      </div>
    );

    content = (
      <div className='gauge-body'>
        {dial}
        {legend && (
          <div className='gauge-legend flex'>
            {legend.map((item) => (
              <div className='legend-item flex' key={item.key}>
                <span
                  className='legend-dot'
                  style={{ backgroundColor: item.color }}
                />
                <span className='legend-title'>{item.title}</span>
                <span className='legend-value'>{item.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <StyledGauge
      className={classNames('ui-gauge', className)}
      data-testid={dataTestId}
    >
      {!noHeader && (
        <div component='CardHeader' className='card-header flex'>
          <div className='flex gap-4'>
            <h3 className='title'>{title}</h3>
            {tooltip && (
              <UiTooltip title={tooltip}>
                <UiIcon
                  icon='ri-information-line'
                  color={theme.palette.secondary.moreLight}
                  size={16}
                />
              </UiTooltip>
            )}
          </div>
          <div className='actions flex'>
            {actions}
            {refresh && (
              <UiIcon onClick={() => refresh()} icon='ri-refresh-line' />
            )}
          </div>
        </div>
      )}
      <div component='CardBody' className='card-body flex'>
        {content}
      </div>
    </StyledGauge>
  );
};

Gauge.propTypes = {
  value: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  colors: PropTypes.arrayOf(PropTypes.string).isRequired,
  formatValue: PropTypes.func,
  minLabel: PropTypes.string,
  maxLabel: PropTypes.string,
  midLabel: PropTypes.string,
  title: PropTypes.string,
  tooltip: PropTypes.string,
  label: PropTypes.string,
  loading: PropTypes.bool,
  error: PropTypes.object,
  noData: PropTypes.bool,
  refresh: PropTypes.func,
  actions: PropTypes.node,
  legend: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string,
      color: PropTypes.string,
      value: PropTypes.node,
    })
  ),
  hoverTooltipContent: PropTypes.node,
  className: PropTypes.string,
  noHeader: PropTypes.bool,
  'data-testid': PropTypes.string,
};

export default Gauge;
